import React from 'react';
import { useRouteMatch } from 'react-router-dom';
import { ChromeIcon, LogoIcon } from './icons';
import { ROUTES } from '../../routes';
import { LayoutFooter } from './components';
import { Button, ButtonSize, ButtonVariant } from '../Button';
import { useMixPanel } from '../MixPanel/useMixPanel';
import { useGoogleAnalytics } from '../GoogleAnalytics/useGoogleAnalytics';
import { Scrollbar } from '../Scrollbar';
import packageInfo from '../../../package.json';
import { MODE } from '../../constants/config';
import { MixpanelEvent } from '../MixPanel/events/events';

const Layout: React.FC = ({ children }) => {
  const homePageMatch = useRouteMatch(ROUTES.HOME);
  const pricingPageMatch = useRouteMatch(ROUTES.PRICING);
  const paymentDetailsPageMatch = useRouteMatch(ROUTES.PAYMENT_DETAILS);
  const uninstallExtenstionPageMatch = useRouteMatch(ROUTES.Q_UNINSTALL);
  const processingPageMatch = useRouteMatch(ROUTES.PROCESSING);

  const { trackEvent } = useMixPanel();
  const { trackGAEvent } = useGoogleAnalytics();

  const showFooter = !!pricingPageMatch || !!uninstallExtenstionPageMatch;
  const showBookADemo = !homePageMatch?.isExact;

  const bookADemoClick = () => {
    if (pricingPageMatch) {
      trackEvent(MixpanelEvent.PRICING_PAGE_BOOK_A_DEMO_BUTTON_CLICK);
      trackGAEvent(
        'event',
        MixpanelEvent.PRICING_PAGE_BOOK_A_DEMO_BUTTON_CLICK
      );
    }
    if (paymentDetailsPageMatch) {
      trackEvent(MixpanelEvent.PAYMENT_DETAILS_PAGE_BOOK_A_DEMO_BUTTON_CLICK);
      trackGAEvent(
        'event',
        MixpanelEvent.PAYMENT_DETAILS_PAGE_BOOK_A_DEMO_BUTTON_CLICK
      );
    }
    if (processingPageMatch) {
      trackEvent(MixpanelEvent.PROCESSING_PAGE_BOOK_A_DEMO_BUTTON_CLICK);
      trackGAEvent(
        'event',
        MixpanelEvent.PROCESSING_PAGE_BOOK_A_DEMO_BUTTON_CLICK
      );
    }
  };

  const attrs = {
    container: {
      className: 'layout',
    },
    header: {
      className: 'layout__header',
    },
    logoLink: {
      className: 'layout__logo-link',
      href: 'https://www.substrata.me/',
      target: '_blank',
      rel: 'noopener noreferrer',
    },
    logo: {
      className: 'layout__logo',
    },
    bookADemo: {
      wpapper: {
        className: 'layout__book-a-demo',
      },
      text: {
        className: 'layout__book-a-demo-text',
      },
      linkWrapper: {
        onClick: bookADemoClick,
      },
      link: {
        className: 'layout__book-a-demo-link',
        href: 'https://calendly.com/yossi-substrata/introduction-call',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    },
    reinstallButton: {
      size: ButtonSize.S,
      variant: ButtonVariant.Secondary,
      className: 'layout__reinstall-button',
      onClick: () => {
        window.open(
          'https://chromewebstore.google.com/detail/substrata-q-for-email/okeemaalpjcfeggfnahbeoagboahbbka',
          '_blank'
        );
      },
    },
    contentWrapper: {
      className: 'layout__content-wrapper',
    },
    scrollable: {
      className: 'layout__scrollable',
    },
    content: {
      className: 'layout__content',
    },
    version: {
      className: 'layout__version',
    },
  };

  const version =
    MODE !== 'prod' ? (
      <div {...attrs.version}>{packageInfo.version}</div>
    ) : null;

  return (
    <div {...attrs.container}>
      <header {...attrs.header}>
        <a {...attrs.logoLink}>
          <LogoIcon {...attrs.logo} />
        </a>
        {showBookADemo ? (
          <div {...attrs.bookADemo.wpapper}>
            <span {...attrs.bookADemo.text}>Not sure yet?</span>
            <span {...attrs.bookADemo.linkWrapper}>
              <a {...attrs.bookADemo.link}>Book a demo</a>
            </span>
          </div>
        ) : null}
      </header>
      <main {...attrs.contentWrapper}>
        <Scrollbar {...attrs.scrollable}>
          <div {...attrs.content}>
            {children}
            {showFooter ? <LayoutFooter /> : null}
          </div>
        </Scrollbar>
      </main>
      {version}
    </div>
  );
};

export default Layout;
